import { jwtDecode } from "jwt-decode"

/**
 * Maps user roles to their default home pages
 */
export const USER_ROLES = {
  ADMIN: "Admin",
  MANAGER: "Manager",
  DSP: "DSP",
  DRIVER: "Driver",
} as const

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES] | string

/**
 * Role to default home page mapping
 */
const DEFAULT_HOME_PAGES: Record<string, string> = {
  [USER_ROLES.ADMIN]: "/applications",
  [USER_ROLES.MANAGER]: "/people",
  [USER_ROLES.DSP]: "/applications",
  [USER_ROLES.DRIVER]: "/sheriff",
  // Add more role mappings as needed
}

export const isDspRole = (role?: string | null): boolean => role === USER_ROLES.DSP

/**
 * Get the default home page for a specific role
 * @param role User role
 * @returns The default home page path for the role, or a fallback path
 */
export const getDefaultHomePageForRole = (role: UserRole): string => {
  return DEFAULT_HOME_PAGES[role] || "/"
}

/** Default home page for the role in a Supabase access token's custom `user_role` claim. */
export const getDefaultHomePageForAccessToken = (accessToken: string): string =>
  getDefaultHomePageForRole(jwtDecode<{ user_role?: string }>(accessToken).user_role ?? "")

/**
 * Determine if a user has access to a specific route based on their role
 * @param role User role
 * @param allowedRoles Array of roles that have access
 * @returns Boolean indicating if the user has access
 */
export const hasRouteAccess = (role: UserRole, allowedRoles: UserRole[]): boolean => {
  if (!allowedRoles || allowedRoles.length === 0) {
    return true // No restrictions
  }

  return allowedRoles.includes(role)
}
